import React, { UIEvent, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronDown, Search } from "lucide-react";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { useDebouncedValue } from "@hooks/useDebouncedValue";
import { ServicesURL } from "@utils/utilsApi";
import { SimpleItemChildrenContainer, SimpleItemChild } from "./style";
import {
  SIDEBAR_CORPORATE_SELECT_TEST_PROPS,
  getCorporateOptionTestProps,
} from "./testIds";

const PAGE_SIZE = 20;
// Distance (px) from the bottom of the list that triggers the next page.
const LOAD_MORE_THRESHOLD = 24;

interface CorporateOption {
  id: number;
  name: string;
}

export interface CorporateSelectProps {
  corporateId: string;
  corporateName: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  onSelect: (corporateId: string) => void;
}

const CorporateSelect: React.FC<CorporateSelectProps> = ({
  corporateId,
  corporateName,
  searchPlaceholder,
  emptyLabel,
  onSelect,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState<CorporateOption[]>([]);

  // The list is fetched only while the panel is open, one page at a time.
  const { items, isLoading } = usePaginatedList<CorporateOption>(
    expanded ? `${ServicesURL.corporate}?status=ACTIVE` : null,
    { page, pageSize: PAGE_SIZE, search: debouncedSearch },
    { searchDebounceMs: 0 },
  );

  useEffect(() => {
    setPage(1);
    setLoaded([]);
  }, [debouncedSearch, expanded]);

  useEffect(() => {
    if (items.length === 0) return;
    setLoaded((prev) => {
      const ids = new Set(prev.map((c) => c.id));
      return [...prev, ...items.filter((c) => !ids.has(c.id))];
    });
  }, [items]);

  const hasMore = items.length === PAGE_SIZE;

  // Without a search the current corporate is pinned on top, so it is always
  // reachable regardless of the order returned by the BE.
  const options = useMemo(() => {
    if (debouncedSearch) return loaded;
    const current = { id: Number(corporateId), name: corporateName };
    return [current, ...loaded.filter((c) => String(c.id) !== corporateId)];
  }, [debouncedSearch, loaded, corporateId, corporateName]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    // Several scroll events can fire before the next render: request the next
    // page only once the current one is merged, and set it absolutely so
    // repeated events cannot skip a page.
    if (
      !isLoading &&
      hasMore &&
      loaded.length >= page * PAGE_SIZE &&
      scrollTop + clientHeight >= scrollHeight - LOAD_MORE_THRESHOLD
    ) {
      setPage(page + 1);
    }
  };

  const handleSelect = (id: string) => {
    setExpanded(false);
    setSearch("");
    if (id !== corporateId) onSelect(id);
  };

  return (
    <Box>
      <Box
        {...SIDEBAR_CORPORATE_SELECT_TEST_PROPS}
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          margin: "0 -10px",
          padding: "4px 10px",
          borderRadius: "10px",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.primary.main,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {corporateName}
        </Typography>
        <ChevronDown
          size={16}
          color={theme.palette.text.tertiary}
          style={{
            flexShrink: 0,
            transition: "transform 0.2s ease",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      {expanded && (
        <Box sx={{ marginTop: "8px" }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={search}
            placeholder={searchPlaceholder}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={14} color={theme.palette.text.tertiary} />
                </InputAdornment>
              ),
              endAdornment: isLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ "& .MuiInputBase-input": { fontSize: "12px" } }}
          />
          <SimpleItemChildrenContainer
            onScroll={handleScroll}
            sx={{
              maxHeight: "350px",
              overflowY: "auto",
              marginTop: "4px",
              "&::-webkit-scrollbar": {
                width: "1px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.divider,
                borderRadius: "4px",
              },
            }}
          >
            {!isLoading && options.length === 0 && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.palette.text.tertiary,
                  padding: "6px 12px",
                }}
              >
                {emptyLabel}
              </Typography>
            )}
            {options.map((corporate) => (
              <SimpleItemChild
                key={corporate.id}
                {...getCorporateOptionTestProps(String(corporate.id))}
                onClick={() => handleSelect(String(corporate.id))}
                sx={{
                  paddingLeft: "12px",
                  backgroundColor:
                    String(corporate.id) === corporateId
                      ? theme.palette.action.selected
                      : "transparent",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.text.primary,
                    lineHeight: 1.4,
                  }}
                >
                  {`${corporate.id} - ${corporate.name}`}
                </Typography>
              </SimpleItemChild>
            ))}
            {isLoading && loaded.length > 0 && (
              <Box
                sx={{ display: "flex", justifyContent: "center", py: "6px" }}
              >
                <CircularProgress size={14} />
              </Box>
            )}
          </SimpleItemChildrenContainer>
        </Box>
      )}
    </Box>
  );
};

export default CorporateSelect;
